import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { Users, Inbox, TrendingUp, Plus, Loader2 } from 'lucide-react';

const pctColor = (p) => {
    if (p >= 70) return 'text-emerald-600 bg-emerald-50';
    if (p >= 40) return 'text-amber-600 bg-amber-50';
    return 'text-rose-600 bg-rose-50';
};

export default function TeacherOverviewPage() {
    const [classes, setClasses] = useState(null);
    const [feed, setFeed] = useState(null);

    useEffect(() => {
        api.get('/api/teacher/classrooms').then((r) => setClasses(r.data)).catch(() => setClasses([]));
        api.get('/api/teacher/submissions?limit=10').then((r) => setFeed(r.data?.items || [])).catch(() => setFeed([]));
    }, []);

    if (classes === null || feed === null) {
        return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-[#7C3AED] animate-spin" /></div>;
    }

    const totalStudents = classes.reduce((a, c) => a + (c.student_count || 0), 0);
    const totalSubs = classes.reduce((a, c) => a + (c.submission_count || 0), 0);
    const avgClassPct = classes.length
        ? Math.round(classes.reduce((a, c) => a + (c.avg_pct || 0), 0) / classes.length)
        : 0;

    return (
        <div className="space-y-8" data-testid="teacher-overview-page">
            <div>
                <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Overview</h1>
                <p className="text-sm text-zinc-500 mt-1">A quick snapshot of your classes and student activity.</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={<Users className="w-5 h-5" />} label="Active classes" value={classes.filter(c => !c.archived).length} testid="stat-classes" />
                <StatCard icon={<Users className="w-5 h-5" />} label="Total students" value={totalStudents} testid="stat-students" />
                <StatCard icon={<Inbox className="w-5 h-5" />} label="Total submissions" value={totalSubs} testid="stat-submissions" />
                <StatCard icon={<TrendingUp className="w-5 h-5" />} label="Avg class score" value={`${avgClassPct}%`} testid="stat-avg" />
            </div>

            {/* Empty state */}
            {classes.length === 0 ? (
                <div className="bg-white rounded-3xl border-2 border-dashed border-zinc-200 p-12 text-center" data-testid="empty-classes">
                    <div className="w-14 h-14 rounded-2xl bg-[#EDE4FF] flex items-center justify-center mx-auto mb-4">
                        <Users className="w-6 h-6 text-[#7C3AED]" />
                    </div>
                    <h3 className="text-lg font-black mb-2">No classes yet</h3>
                    <p className="text-sm text-zinc-500 mb-5">Create your first class to start receiving student submissions.</p>
                    <Link to="/teacher/classes" className="inline-flex items-center gap-2 bg-[#7C3AED] text-white font-bold text-sm px-5 py-2.5 rounded-xl hover:bg-[#6D28D9]" data-testid="create-first-class">
                        <Plus className="w-4 h-4" /> Create a class
                    </Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* My classes */}
                    <div className="bg-white rounded-3xl border border-zinc-200 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-black text-base">My classes</h2>
                            <Link to="/teacher/classes" className="text-xs font-bold text-[#7C3AED]">See all →</Link>
                        </div>
                        <ul className="space-y-2.5">
                            {classes.slice(0, 5).map((c) => (
                                <li key={c.id}>
                                    <Link to={`/teacher/classes/${c.id}`} className="flex items-center justify-between p-3 rounded-xl hover:bg-[#F8F7FC] transition-colors group" data-testid={`overview-class-${c.id}`}>
                                        <div className="min-w-0">
                                            <div className="font-bold text-sm truncate">{c.name}</div>
                                            <div className="text-xs text-zinc-500 truncate">{c.subject} · {c.student_count} students</div>
                                        </div>
                                        <span className={`text-xs font-black px-2 py-1 rounded-md ${pctColor(c.avg_pct || 0)}`}>{Math.round(c.avg_pct || 0)}%</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Recent submissions */}
                    <div className="bg-white rounded-3xl border border-zinc-200 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-black text-base">Recent submissions</h2>
                            <Link to="/teacher/submissions" className="text-xs font-bold text-[#7C3AED]">See all →</Link>
                        </div>
                        {feed.length === 0 ? (
                            <p className="text-sm text-zinc-500 py-8 text-center">No submissions yet.</p>
                        ) : (
                            <ul className="space-y-2.5">
                                {feed.slice(0, 6).map((s) => (
                                    <li key={s.id}>
                                        <Link to={`/teacher/submissions/${s.id}`} className="flex items-center justify-between p-3 rounded-xl hover:bg-[#F8F7FC] group" data-testid={`overview-sub-${s.id}`}>
                                            <div className="min-w-0">
                                                <div className="font-bold text-sm truncate">{s.student_name || 'Student'}</div>
                                                <div className="text-xs text-zinc-500 truncate">{s.subject} · {s.question_snippet || ''}</div>
                                            </div>
                                            <span className={`text-xs font-black px-2 py-1 rounded-md ${pctColor(s.pct)}`}>{Math.round(s.pct)}%</span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function StatCard({ icon, label, value, testid }) {
    return (
        <div className="bg-white rounded-2xl border border-zinc-200 p-5" data-testid={testid}>
            <div className="w-9 h-9 rounded-xl bg-[#EDE4FF] flex items-center justify-center text-[#7C3AED] mb-3">{icon}</div>
            <div className="text-2xl font-black">{value}</div>
            <div className="text-xs text-zinc-500 mt-0.5">{label}</div>
        </div>
    );
}
